import { EmptyState, LoadingState, OutletIndicator } from '@components/common';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectAllOutlets,
  selectEffectiveOutlet,
  selectEffectiveOutletId,
} from '@store/outlets';
import { ShieldCheck } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSecuritySnapshot,
  saveRolePolicyAssignment,
} from '../../../services/accessControlService';
import { selectCurrentUser } from '../../../store/auth/authSelectors';
import { fetchCurrentUser } from '../../../store/auth/authThunks';
import {
  ASSIGNABLE_POLICY_ROLES,
  SecuritySnapshot,
} from '../../../types/accessControl';
import { Outlet } from '../../../types/outlet';
import { apiError, apiSuccess } from '../../../util/notify';
import { RoleAssignmentSection } from './RoleAssignmentSection';

const EMPTY_SECURITY_SNAPSHOT: SecuritySnapshot = {
  controls: [],
  policies: [],
  assignments: [],
};

const formatRoleLabel = (value: string) =>
  value
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

export const RoleAssignmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const outlets = useAppSelector(selectAllOutlets);
  const selectedOutletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);

  const [snapshot, setSnapshot] = useState<SecuritySnapshot>(
    EMPTY_SECURITY_SNAPSHOT,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [savingRoleKey, setSavingRoleKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const companyId = currentUser?.company?.id ?? null;
  const currentUserId = currentUser?.id ?? null;

  const loadAssignments = useCallback(async () => {
    if (!companyId) {
      setError('Company details are missing for this account.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const response = await fetchSecuritySnapshot(companyId, selectedOutletId);

    if (!response.success || !response.data) {
      setError(response.error || 'Failed to load role assignments');
      setSnapshot(EMPTY_SECURITY_SNAPSHOT);
    } else {
      setSnapshot(response.data);
      setError(null);
    }

    setIsLoading(false);
  }, [companyId, selectedOutletId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const outletsToShow = useMemo(() => {
    if (selectedOutlet) {
      return [selectedOutlet];
    }

    return outlets;
  }, [outlets, selectedOutlet]);

  const assignmentsByOutlet = useMemo(() => {
    return snapshot.assignments.reduce<
      Record<string, Record<string, string | null>>
    >((accumulator, assignment) => {
      accumulator[assignment.outlet_id] =
        accumulator[assignment.outlet_id] || {};
      accumulator[assignment.outlet_id][assignment.role] = assignment.policy_id;
      return accumulator;
    }, {});
  }, [snapshot.assignments]);

  const handleSaveAssignment = async (
    outletId: string,
    role: (typeof ASSIGNABLE_POLICY_ROLES)[number],
    policyId: string | null,
  ) => {
    if (!companyId || !currentUserId) {
      apiError('Missing company or user information for this action');
      return;
    }

    const roleKey = `${outletId}:${role}`;
    setSavingRoleKey(roleKey);

    const response = await saveRolePolicyAssignment({
      companyId,
      outletId,
      role,
      policyId,
      assignedBy: currentUserId,
    });

    setSavingRoleKey(null);

    if (!response.success) {
      apiError(response.error || 'Failed to save role assignment');
      return;
    }

    apiSuccess(
      policyId
        ? `${formatRoleLabel(role)} policy saved successfully`
        : `${formatRoleLabel(role)} policy cleared successfully`,
    );
    await dispatch(fetchCurrentUser());
    await loadAssignments();
  };

  if (isLoading) {
    return <LoadingState message="Loading role assignments..." />;
  }

  return (
    <div className="space-y-6">
      <div className="">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Role policy assignments
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Assign a policy to each restricted role for the selected outlet.
            </p>
          </div>
          <div className="rounded-2xl border border-pos-primary/15 bg-app-bg/50 px-4 py-3 text-sm text-gray-700 w-fit max-w-sm">
            <div className="flex items-center gap-2 font-semibold text-black">
              <ShieldCheck size={16} className="text-pos-primary" />
              Unrestricted roles
            </div>
            <p className="mt-1 text-xs leading-5 text-gray-600">
              Super users always have full access and do not need policy
              assignments.
            </p>
          </div>
        </div>
        <div className="mt-4">
          <OutletIndicator description="No outlet selected. Showing role assignments for all outlets in this company." />
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {!snapshot.policies.length ? (
        <EmptyState
          title="No policies available yet"
          description="Create at least one policy before assigning access rules to outlet roles."
        />
      ) : !outletsToShow.length ? (
        <EmptyState
          title="No outlets available"
          description="Create an outlet before assigning role policies."
        />
      ) : (
        <div className="space-y-4">
          {outletsToShow.map((outlet: Outlet) => (
            <RoleAssignmentSection
              key={outlet.id}
              outlet={outlet}
              policies={snapshot.policies}
              assignedPolicyIds={assignmentsByOutlet[outlet.id] || {}}
              savingRoleKey={savingRoleKey}
              onSave={handleSaveAssignment}
            />
          ))}
        </div>
      )}
    </div>
  );
};
